/**
 * THE THREE CATEGORIES — the half of the changelog a CLIENT may hold.
 *
 * `lib/changelog.ts` reads the files off disk and therefore imports `fs`, which
 * makes it server-only by construction: importing it from a `"use client"`
 * component is a build error, and that is the intended guard rail. But the feed's
 * card is a client component and still needs the category names to write "3 ting
 * fikset" — so the names live here, in a module with no filesystem in it, and
 * both sides import from one place.
 *
 * (This file exists because the guard rail fired: the card first imported the
 * constants from `changelog.ts` directly, and `next build` refused with
 * `Module not found: Can't resolve 'fs'` for the client bundle. The split is the
 * fix the guard rail was asking for.)
 */

/**
 * `tilfoejet` is something that was not possible before, `aendret` is something
 * that already worked and now works differently, `fikset` is something that was
 * wrong.
 *
 * ASCII keys on purpose — a JSON key with `ø` in it is one somebody eventually
 * mistypes, and a mistyped key is not a type error in a hand-written file. The
 * Danish names live below, where a reader sees them.
 */
export const CATEGORIES = ["tilfoejet", "aendret", "fikset"] as const;
export type Category = (typeof CATEGORIES)[number];

/** The heading a group of lines sits under on `/om`. */
export const CATEGORY_LABEL: Record<Category, string> = {
  tilfoejet: "Tilføjet",
  aendret: "Ændret",
  fikset: "Fikset",
};

/** The word in a tally — "3 ting tilføjet" — which is a count, not a heading. */
export const CATEGORY_COUNT_LABEL: Record<Category, string> = {
  tilfoejet: "tilføjet",
  aendret: "ændret",
  fikset: "fikset",
};

export type CategoryCounts = Record<Category, number>;

/**
 * "3 ting tilføjet, 2 ting ændret og 4 ting fikset".
 *
 * A category with nothing in it is left out: "0 ting fikset" is noise rather
 * than information. Shared because `/om` and the feed's card both say it, and
 * two copies of a sentence are two sentences that drift.
 */
export function tallySentence(counts: CategoryCounts): string {
  const parts = CATEGORIES.filter((c) => counts[c] > 0).map(
    (c) => `${counts[c]} ting ${CATEGORY_COUNT_LABEL[c]}`,
  );
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} og ${parts[parts.length - 1]}`;
}
